import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useAuth } from './AuthContext';

const TabGuardContext = createContext({ isDuplicateTab: false, claimPrimary: () => {} });

const LOCK_NAME      = 'ptp_tab_lock';
const SESSION_ID_KEY = 'ptp_tab_id';      // sessionStorage — unique per tab, survives page refresh
const WAS_PRIMARY    = 'ptp_was_primary'; // sessionStorage — "I was primary before this refresh"
const SIGNAL_KEY     = 'ptp_tab_signal';  // localStorage  — tab-to-tab yield signals

// Write a yield signal so the current lock-holder releases to us.
// Used for: (1) force-login takeover, (2) refresh reclaim.
const sendYieldSignal = (fromTabId) => {
  const value = JSON.stringify({ from: fromTabId });
  localStorage.setItem(SIGNAL_KEY, value);
  // Auto-clean after 2 s in case nobody consumed it
  setTimeout(() => {
    if (localStorage.getItem(SIGNAL_KEY) === value) localStorage.removeItem(SIGNAL_KEY);
  }, 2000);
};

export const TabGuardProvider = ({ children }) => {
  const { isAuthenticated } = useAuth();
  const [isDuplicateTab, setIsDuplicateTab] = useState(false);

  const tabIdRef   = useRef(null);
  const releaseRef = useRef(null);   // resolves the lock-holding promise → releases the lock
  const isPrimary  = useRef(false);

  if (!tabIdRef.current) {
    let id = sessionStorage.getItem(SESSION_ID_KEY);
    if (!id) {
      id = Math.random().toString(36).slice(2, 11);
      sessionStorage.setItem(SESSION_ID_KEY, id);
    }
    tabIdRef.current = id;
  }

  // Exposed to Login.jsx: call after any successful login to claim the primary role.
  // Signals whoever currently holds the lock to release it.
  const claimPrimary = () => sendYieldSignal(tabIdRef.current);

  useEffect(() => {
    if (!isAuthenticated) {
      // Release lock on logout / session expiry / kick
      releaseRef.current?.();
      releaseRef.current = null;
      isPrimary.current  = false;
      setIsDuplicateTab(false);
      sessionStorage.removeItem(WAS_PRIMARY);
      return;
    }

    // Graceful degradation: if Web Locks aren't supported (very old browsers),
    // skip the tab guard entirely — API-level single-device enforcement still applies.
    if (!navigator.locks) return;

    const tabId = tabIdRef.current;
    const abort = new AbortController();
    let   granted = false;

    // ─ Storage signal listener ─
    // The primary tab releases its lock when it receives a yield signal from
    // another tab (force-login or refresh-reclaim).
    const handleSignal = (e) => {
      if (e.key !== SIGNAL_KEY || !e.newValue || !isPrimary.current) return;
      try {
        const sig = JSON.parse(e.newValue);
        if (sig.from !== tabId) releaseRef.current?.();
      } catch { /* ignore malformed signal */ }
    };
    window.addEventListener('storage', handleSignal);

    // ─ Refresh reclaim ─
    // If this tab was primary before a page refresh, the lock was briefly grabbed
    // by the next-in-queue tab while we were reloading.  Send a yield signal
    // AFTER our own lock request is queued (100 ms grace) so we are next in line
    // when the current holder releases.
    if (sessionStorage.getItem(WAS_PRIMARY) === '1') {
      setTimeout(() => {
        if (!granted) sendYieldSignal(tabId);
      }, 100);
    }

    // ─ Lock request ─
    navigator.locks.request(LOCK_NAME, { signal: abort.signal }, async () => {
      // If a pending yield signal from another tab is present, release immediately
      // so the signalling tab can get the lock next (handles the refresh race).
      const pending = localStorage.getItem(SIGNAL_KEY);
      if (pending) {
        try {
          const sig = JSON.parse(pending);
          if (sig.from !== tabId) return; // yield — our lock callback resolves, lock released
        } catch { /* ignore */ }
      }

      granted = true;
      isPrimary.current = true;
      sessionStorage.setItem(WAS_PRIMARY, '1');
      setIsDuplicateTab(false);

      // Hold the lock until explicitly released
      await new Promise(resolve => { releaseRef.current = resolve; });

      isPrimary.current = false;
    }).catch(err => {
      if (err.name !== 'AbortError') console.error('[TabGuard] Lock error:', err);
    });

    // ─ Duplicate screen timer ─
    // If the lock isn't granted within the grace period, we are a duplicate tab.
    // A previously-primary tab (refresh) gets a longer grace period because it
    // needs time to queue its lock request and send the reclaim signal.
    const wasPrimary = sessionStorage.getItem(WAS_PRIMARY) === '1';
    const duplicateDelay = wasPrimary ? 600 : 60;
    const timer = setTimeout(() => {
      if (!granted) setIsDuplicateTab(true);
    }, duplicateDelay);

    return () => {
      clearTimeout(timer);
      abort.abort();                  // cancels any pending (not yet granted) lock request
      window.removeEventListener('storage', handleSignal);
      releaseRef.current?.();         // releases the lock if this tab held it
      releaseRef.current = null;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]);

  return (
    <TabGuardContext.Provider value={{ isDuplicateTab, claimPrimary }}>
      {children}
    </TabGuardContext.Provider>
  );
};

export const useTabGuard = () => useContext(TabGuardContext);
